/**
 * FeatureTour
 *
 * Orchestrator component for the onboarding feature tour.
 * Mounts in App.tsx and auto-triggers on first game entry.
 * Defers if TownCrier is visible.
 *
 * Keyboard: Right/Enter = next, Left = prev, Escape = dismiss
 */

import { useEffect, useCallback } from 'react';
import { useFeatureTourStore } from '../../stores/featureTourStore';
import { getTourById } from './tourSteps';
import { TourSpotlight } from './TourSpotlight';
import { TourSlide } from './TourSlide';

interface FeatureTourProps {
  /** Is the current user a GM? */
  isGM: boolean;
}

export function FeatureTour({ isGM }: FeatureTourProps) {
  const {
    isActive,
    currentTourId,
    currentStepIndex,
    startTour,
    nextStep,
    prevStep,
    completeTour,
    dismissTour,
    isTourCompleted,
  } = useFeatureTourStore();

  // Auto-trigger on mount (with delay to let the scene settle)
  useEffect(() => {
    const timer = setTimeout(() => {
      // Don't trigger if TownCrier is visible
      if (document.querySelector('.town-crier-scroll')) return;

      const tourId = isGM ? 'gm-intro' : 'player-intro';
      if (!isTourCompleted(tourId)) {
        startTour(tourId);
      }
    }, 800);

    return () => clearTimeout(timer);
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get current tour data
  const tour = currentTourId ? getTourById(currentTourId) : undefined;
  const steps = tour?.steps ?? [];
  const currentStep = steps[currentStepIndex];

  // Navigation handlers
  const handleNext = useCallback(() => {
    if (currentStepIndex >= steps.length - 1) {
      completeTour();
    } else {
      nextStep();
    }
  }, [currentStepIndex, steps.length, completeTour, nextStep]);

  const handlePrev = useCallback(() => {
    prevStep();
  }, [prevStep]);

  const handleDismiss = useCallback(() => {
    dismissTour();
  }, [dismissTour]);

  // Keyboard navigation
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'Enter':
          e.preventDefault();
          handleNext();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          handlePrev();
          break;
        case 'Escape':
          e.preventDefault();
          handleDismiss();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isActive, handleNext, handlePrev, handleDismiss]);

  // Don't render if tour isn't active or data is missing
  if (!isActive || !currentStep || !tour) return null;

  return (
    <>
      {/* Dark overlay with spotlight cutout */}
      <TourSpotlight
        selector={currentStep.spotlight}
        onClick={handleNext}
      />

      {/* Content slide */}
      <TourSlide
        step={currentStep}
        stepIndex={currentStepIndex}
        totalSteps={steps.length}
        isFirst={currentStepIndex === 0}
        isLast={currentStepIndex === steps.length - 1}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={handleDismiss}
        spotlightSelector={currentStep.spotlight}
      />
    </>
  );
}
